(() => {
  const SVG_NS = "http://www.w3.org/2000/svg";
  const instances = new Map();
  let resizeBound = false;

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function getNumber(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function getConfig(container) {
    const radius = getNumber(container.dataset.highlightV3Radius, 15);
    const isMobile = window.innerWidth < 768;
    const padX = isMobile
      ? getNumber(container.dataset.highlightV3PadXMobile, getNumber(container.dataset.highlightV3PadX, 12))
      : getNumber(container.dataset.highlightV3PadX, 20);
    const padY = isMobile
      ? getNumber(container.dataset.highlightV3PadYMobile, getNumber(container.dataset.highlightV3PadY, 8))
      : getNumber(container.dataset.highlightV3PadY, 14);
    return {
      align: container.dataset.highlightV3Align === "left" ? "left" : "center",
      fill: container.dataset.highlightV3Fill || "#190E03",
      padX,
      padY,
      radius,
      extraJoinDepth: getNumber(container.dataset.highlightV3ExtraJoinDepth, 12),
      minStepForCorner: radius,
    };
  }

  function getComputedLineHeight(el) {
    const styles = window.getComputedStyle(el);
    const lineHeight = styles.lineHeight;
    if (lineHeight !== "normal") return parseFloat(lineHeight);
    return parseFloat(styles.fontSize) * 1.2;
  }

  function getRawFragments(el, wrapRect) {
    const range = document.createRange();
    range.selectNodeContents(el);
    return Array.from(range.getClientRects())
      .filter((rect) => rect.width > 1 && rect.height > 1)
      .map((rect) => ({
        left: rect.left - wrapRect.left,
        right: rect.right - wrapRect.left,
        top: rect.top - wrapRect.top,
        bottom: rect.bottom - wrapRect.top,
        centerY: (rect.top + rect.bottom) / 2 - wrapRect.top,
      }));
  }

  function groupFragmentsIntoLines(fragments, lineHeight) {
    if (!fragments.length) return [];
    const sorted = [...fragments].sort((a, b) => a.centerY - b.centerY);
    const tolerance = Math.max(8, lineHeight * 0.45);
    const lines = [];

    for (const frag of sorted) {
      const last = lines[lines.length - 1];
      if (last && Math.abs(last.centerY - frag.centerY) < tolerance) {
        last.left = Math.min(last.left, frag.left);
        last.right = Math.max(last.right, frag.right);
        last.top = Math.min(last.top, frag.top);
        last.bottom = Math.max(last.bottom, frag.bottom);
        last.centerY = (last.centerY + frag.centerY) / 2;
      } else {
        lines.push({ ...frag });
      }
    }
    return lines;
  }

  function inferLineStep(lines, fallbackLineHeight) {
    if (lines.length < 2) return fallbackLineHeight;
    const diffs = [];
    for (let i = 1; i < lines.length; i += 1) {
      diffs.push(lines[i].centerY - lines[i - 1].centerY);
    }
    const average = diffs.reduce((sum, v) => sum + v, 0) / diffs.length;
    return Number.isFinite(average) && average > 0 ? average : fallbackLineHeight;
  }

  function buildNormalizedLineBoxes(lines, lineStep, config) {
    if (!lines.length) return [];
    const maxTextHeight = Math.max(...lines.map((l) => l.bottom - l.top));
    const boxHeight = Math.max(
      maxTextHeight + config.padY * 2,
      lineStep + config.extraJoinDepth,
    );
    const firstCenter = lines[0].centerY;

    return lines.map((line, index) => {
      const cy = firstCenter + index * lineStep;
      return {
        x1: line.left - config.padX,
        x2: line.right + config.padX,
        y1: cy - boxHeight / 2,
        y2: cy + boxHeight / 2,
        cy,
      };
    });
  }

  function normalizeSmallSideSteps(lines, minStep) {
    const adjusted = lines.map((l) => ({ ...l }));

    for (let pass = 0; pass < 6; pass += 1) {
      let changed = false;

      for (let i = 0; i < adjusted.length - 1; i += 1) {
        const curr = adjusted[i];
        const next = adjusted[i + 1];

        const leftDelta = Math.abs(curr.x1 - next.x1);
        if (leftDelta > 0 && leftDelta < minStep) {
          const sharedLeft = Math.min(curr.x1, next.x1);
          curr.x1 = sharedLeft;
          next.x1 = sharedLeft;
          changed = true;
        }

        const rightDelta = Math.abs(curr.x2 - next.x2);
        if (rightDelta > 0 && rightDelta < minStep) {
          const sharedRight = Math.max(curr.x2, next.x2);
          curr.x2 = sharedRight;
          next.x2 = sharedRight;
          changed = true;
        }
      }

      if (!changed) break;
    }

    return adjusted;
  }

  function normalizeLeftAlignedLeadingEdge(lines) {
    if (!lines.length) return [];
    const sharedLeft = Math.min(...lines.map((l) => l.x1));
    return lines.map((l) => ({ ...l, x1: sharedLeft }));
  }

  function getCornerRadius(baseRadius, horizontalStep, verticalSpan) {
    return clamp(
      baseRadius,
      0,
      Math.min(Math.abs(horizontalStep) / 2, Math.abs(verticalSpan) / 2, baseRadius),
    );
  }

  function buildRoundedSteppedPath(lines, baseRadius) {
    if (!lines.length) return "";

    const first = lines[0];
    const last = lines[lines.length - 1];

    const topR = Math.min(baseRadius, (first.x2 - first.x1) / 2, (first.y2 - first.y1) / 2);

    let d = `M ${first.x1 + topR} ${first.y1}`;
    d += `H ${first.x2 - topR}`;
    d += `Q ${first.x2} ${first.y1} ${first.x2} ${first.y1 + topR}`;

    for (let i = 0; i < lines.length - 1; i += 1) {
      const curr = lines[i];
      const next = lines[i + 1];
      const step = next.x2 - curr.x2;
      const r = getCornerRadius(baseRadius, step, next.cy - curr.cy);
      let joinY;

      if (step > 0) {
        // Outward step: anchor at the TOP of the next (wider) line's box so the
        // transition completes before line 2's text starts, preventing overflow.
        joinY = next.y1;
      } else {
        joinY = curr.y2;
      }

      if (next.x2 < curr.x2) {
        d += `V ${joinY - r}`;
        d += `Q ${curr.x2} ${joinY} ${curr.x2 - r} ${joinY}`;
        d += `H ${next.x2 + r}`;
        d += `Q ${next.x2} ${joinY} ${next.x2} ${joinY + r}`;
      } else if (next.x2 > curr.x2) {
        d += `V ${joinY - r}`;
        d += `Q ${curr.x2} ${joinY} ${curr.x2 + r} ${joinY}`;
        d += `H ${next.x2 - r}`;
        d += `Q ${next.x2} ${joinY} ${next.x2} ${joinY + r}`;
      } else {
        d += `V ${joinY}`;
      }
    }

    const bottomR = Math.min(baseRadius, (last.x2 - last.x1) / 2, (last.y2 - last.y1) / 2);

    d += `V ${last.y2 - bottomR}`;
    d += `Q ${last.x2} ${last.y2} ${last.x2 - bottomR} ${last.y2}`;
    d += `H ${last.x1 + bottomR}`;
    d += `Q ${last.x1} ${last.y2} ${last.x1} ${last.y2 - bottomR}`;

    for (let i = lines.length - 1; i > 0; i -= 1) {
      const curr = lines[i];
      const prev = lines[i - 1];
      const step = prev.x1 - curr.x1;
      const r = getCornerRadius(baseRadius, step, curr.cy - prev.cy);
      let joinY;

      if (step > 0) {
        // Outward step on the left side: mirror the forward loop — anchor at the
        // top of the current (wider/lower) line's box.
        joinY = curr.y1;
      } else {
        joinY = prev.y2;
      }

      if (prev.x1 < curr.x1) {
        d += `V ${joinY + r}`;
        d += `Q ${curr.x1} ${joinY} ${curr.x1 - r} ${joinY}`;
        d += `H ${prev.x1 + r}`;
        d += `Q ${prev.x1} ${joinY} ${prev.x1} ${joinY - r}`;
      } else if (prev.x1 > curr.x1) {
        d += `V ${joinY + r}`;
        d += `Q ${curr.x1} ${joinY} ${curr.x1 + r} ${joinY}`;
        d += `H ${prev.x1 - r}`;
        d += `Q ${prev.x1} ${joinY} ${prev.x1} ${joinY - r}`;
      } else {
        d += `V ${joinY}`;
      }
    }

    d += `V ${first.y1 + topR}`;
    d += `Q ${first.x1} ${first.y1} ${first.x1 + topR} ${first.y1}`;
    d += "Z";
    return d;
  }

  function drawHighlight(instance) {
    const { container, headline, svg } = instance;
    const config = getConfig(container);
    const wrapRect = container.getBoundingClientRect();
    if (!wrapRect.width || !wrapRect.height) return;

    svg.innerHTML = "";
    const lineHeight = getComputedLineHeight(headline);
    const fragments = getRawFragments(headline, wrapRect);
    const groupedLines = groupFragmentsIntoLines(fragments, lineHeight);
    if (!groupedLines.length) return;

    const lineStep = inferLineStep(groupedLines, lineHeight);
    const normalizedLines = buildNormalizedLineBoxes(groupedLines, lineStep, config);
    const alignmentAdjustedLines =
      config.align === "left"
        ? normalizeLeftAlignedLeadingEdge(normalizedLines)
        : normalizedLines;
    const cleanedLines = normalizeSmallSideSteps(alignmentAdjustedLines, config.minStepForCorner);
    const pathData = buildRoundedSteppedPath(cleanedLines, config.radius);
    if (!pathData) return;

    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", pathData);
    path.setAttribute("fill", config.fill);
    svg.appendChild(path);
  }

  function setupHighlight(container) {
    if (instances.has(container)) {
      drawHighlight(instances.get(container));
      return;
    }

    const headline = container.querySelector(".highlight-title-v3__text");
    const svg = container.querySelector(".highlight-title-v3__svg");
    if (!headline || !svg) return;

    const instance = { container, headline, svg, draw: null, observer: null };
    instance.draw = () => drawHighlight(instance);
    drawHighlight(instance);

    if ("ResizeObserver" in window) {
      instance.observer = new ResizeObserver(instance.draw);
      instance.observer.observe(headline);
      instance.observer.observe(container);
    }

    instances.set(container, instance);
  }

  function initHighlightTitles(root = document) {
    root.querySelectorAll("[data-highlight-v3]").forEach(setupHighlight);
  }

  function redrawAll() {
    instances.forEach((instance) => instance.draw());
  }

  document.addEventListener("DOMContentLoaded", async () => {
    initHighlightTitles(document);
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
      redrawAll();
    }
  });

  document.addEventListener("shopify:section:load", (event) => {
    initHighlightTitles(event.target);
    redrawAll();
  });

  if (!resizeBound) {
    resizeBound = true;
    let rafId = null;
    window.addEventListener("resize", () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        redrawAll();
      });
    });
  }
})();
